import { useEffect } from 'react';
import { Button, Card, Col, Form, Input, Row, Space, Spin, Switch, message } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { accountsApi, type AccountInput } from '../../../api/endpoints/accounts';
import { PageHeader } from '../../../components/common/PageHeader';
import { LookupAutoComplete } from '../../../components/common/LookupAutoComplete';
import { AccountSelect } from '../../../components/common/AccountSelect';
import { UserSelect } from '../../../components/common/UserSelect';
import { CountrySelect } from '../../../components/common/CountrySelect';

const str = (max: number) => z.string().max(max).optional().or(z.literal(''));

const schema = z.object({
  name: z.string().min(1, 'Nombre obligatorio').max(120),
  shortName: str(80),
  taxId: str(40),
  parentId: z.number().int().nullable().optional(),
  assignedToId: z.number().int().nullable().optional(),
  businessLine: str(60),
  businessLine2: str(60),
  businessLine3: str(60),
  category: str(60),
  categoryAlt: str(60),
  email: z.string().email('Correo inválido').optional().or(z.literal('')),
  phone: str(30),
  phoneAlt: str(30),
  fax: str(30),
  website: str(120),
  addressLine1: str(120),
  addressLine2: str(120),
  zip: str(20),
  city: str(60),
  state: str(60),
  region: str(60),
  countryId: z.number().int().nullable().optional(),
  archived: z.boolean().optional(),
  notes: z.string().optional().or(z.literal('')),
  // Contacto principal (solo al crear)
  pcFirstName: str(60),
  pcLastName: str(60),
  pcJobTitle: str(60),
  pcEmail: z.string().email('Correo inválido').optional().or(z.literal('')),
  pcPhone: str(30),
  pcMobile: str(30),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  name: '',
  shortName: '',
  taxId: '',
  parentId: null,
  assignedToId: null,
  businessLine: '',
  businessLine2: '',
  businessLine3: '',
  category: '',
  categoryAlt: '',
  email: '',
  phone: '',
  phoneAlt: '',
  fax: '',
  website: '',
  addressLine1: '',
  addressLine2: '',
  zip: '',
  city: '',
  state: '',
  region: '',
  countryId: null,
  archived: false,
  notes: '',
  pcFirstName: '',
  pcLastName: '',
  pcJobTitle: '',
  pcEmail: '',
  pcPhone: '',
  pcMobile: '',
};

const ACCOUNT_STRING_FIELDS: Array<keyof AccountInput> = [
  'shortName', 'taxId', 'businessLine', 'businessLine2', 'businessLine3',
  'category', 'categoryAlt', 'email', 'phone', 'phoneAlt', 'fax', 'website',
  'addressLine1', 'addressLine2', 'zip', 'city', 'state', 'region', 'notes',
];

function sanitize(values: FormValues, isEdit: boolean): AccountInput {
  const out: Record<string, unknown> = { name: values.name };
  for (const k of ACCOUNT_STRING_FIELDS) {
    const v = values[k as keyof FormValues];
    if (typeof v === 'string' && v !== '') out[k] = v;
  }
  if (values.parentId != null) out.parentId = values.parentId;
  if (values.assignedToId != null) out.assignedToId = values.assignedToId;
  if (values.countryId != null) out.countryId = values.countryId;
  if (values.archived) out.archived = true;

  // Contacto principal solo al crear y si hay apellido.
  if (!isEdit && values.pcLastName && values.pcLastName.trim() !== '') {
    out.primaryContact = {
      lastName: values.pcLastName.trim(),
      ...(values.pcFirstName ? { firstName: values.pcFirstName } : {}),
      ...(values.pcJobTitle ? { jobTitle: values.pcJobTitle } : {}),
      ...(values.pcEmail ? { email: values.pcEmail } : {}),
      ...(values.pcPhone ? { phone: values.pcPhone } : {}),
      ...(values.pcMobile ? { mobile: values.pcMobile } : {}),
    };
  }
  return out as AccountInput;
}

export function AccountEditPage(): JSX.Element {
  const { id } = useParams();
  const accountId = id ? parseInt(id, 10) : null;
  const isEdit = accountId !== null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountsApi.findOne(accountId!),
    enabled: isEdit,
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (data) {
      reset({
        ...EMPTY,
        name: data.name,
        shortName: data.shortName ?? '',
        taxId: data.taxId ?? '',
        parentId: data.parentId,
        assignedToId: data.assignedToId,
        businessLine: data.businessLine ?? '',
        businessLine2: data.businessLine2 ?? '',
        businessLine3: data.businessLine3 ?? '',
        category: data.category ?? '',
        categoryAlt: data.categoryAlt ?? '',
        email: data.email ?? '',
        phone: data.phone ?? '',
        phoneAlt: data.phoneAlt ?? '',
        fax: data.fax ?? '',
        website: data.website ?? '',
        addressLine1: data.addressLine1 ?? '',
        addressLine2: data.addressLine2 ?? '',
        zip: data.zip ?? '',
        city: data.city ?? '',
        state: data.state ?? '',
        region: data.region ?? '',
        countryId: data.countryId,
        archived: data.archived,
        notes: data.notes ?? '',
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = sanitize(values, isEdit);
      if (isEdit) return accountsApi.update(accountId, payload);
      return accountsApi.create(payload);
    },
    onSuccess: (saved) => {
      message.success(isEdit ? 'Cuenta actualizada' : 'Cuenta creada');
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['account', saved.id] });
      void queryClient.invalidateQueries({ queryKey: ['persons'] });
      navigate(`/cuentas/${saved.id}`);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo guardar la cuenta';
      message.error(msg);
    },
  });

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const text = (name: keyof FormValues, label: string, required = false): JSX.Element => (
    <Col xs={24} md={12}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <Form.Item
            label={label}
            required={required}
            validateStatus={fieldState.error ? 'error' : ''}
            help={fieldState.error?.message}
          >
            <Input {...field} value={(field.value as string) ?? ''} />
          </Form.Item>
        )}
      />
    </Col>
  );

  return (
    <>
      <PageHeader
        title={isEdit ? `Editar cuenta` : 'Nueva cuenta'}
        crumbs={[
          { title: 'Cuentas', to: '/cuentas' },
          { title: isEdit ? data?.name ?? '' : 'Nueva' },
        ]}
      />
      <Form layout="vertical" onFinish={handleSubmit((v) => mutation.mutate(v))}>
        <Card title="Datos generales" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            {text('name', 'Nombre', true)}
            {text('shortName', 'Nombre corto')}
            {text('taxId', 'RFC / Tax ID')}
            <Col xs={24} md={12}>
              <Controller
                name="parentId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Cuenta padre">
                    <AccountSelect value={field.value ?? null} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="assignedToId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Asignada a">
                    <UserSelect value={field.value ?? null} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="archived"
                control={control}
                render={({ field }) => (
                  <Form.Item label="¿Archivada?">
                    <Switch checked={field.value ?? false} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Clasificación" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Controller
                name="businessLine"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Giro">
                    <LookupAutoComplete
                      listName="account_business1"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Ej. Tecnología, Manufactura…"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            {text('businessLine2', 'Giro 2')}
            {text('businessLine3', 'Giro 3')}
            <Col xs={24} md={12}>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Categoría">
                    <LookupAutoComplete
                      listName="account_category"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Ej. Cliente activo, Prospecto…"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            {text('categoryAlt', 'Categoría alterna')}
          </Row>
        </Card>

        <Card title="Contacto (empresa)" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            {text('email', 'Correo')}
            {text('phone', 'Teléfono')}
            {text('phoneAlt', 'Teléfono alterno')}
            {text('fax', 'Fax')}
            {text('website', 'Sitio web')}
          </Row>
        </Card>

        <Card title="Dirección" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            {text('addressLine1', 'Calle y número')}
            {text('addressLine2', 'Línea 2')}
            {text('zip', 'C.P.')}
            {text('city', 'Ciudad')}
            {text('state', 'Estado')}
            {text('region', 'Región')}
            <Col xs={24} md={12}>
              <Controller
                name="countryId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="País">
                    <CountrySelect value={field.value ?? null} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        {!isEdit && (
          <Card title="Contacto principal (opcional)" style={{ marginBottom: 16 }}>
            <p style={{ marginTop: 0, color: '#888' }}>
              Si capturas un apellido, se crea también el contacto principal de la cuenta.
            </p>
            <Row gutter={16}>
              {text('pcFirstName', 'Nombre(s)')}
              {text('pcLastName', 'Apellidos')}
              {text('pcJobTitle', 'Puesto')}
              {text('pcEmail', 'Correo')}
              {text('pcPhone', 'Teléfono')}
              {text('pcMobile', 'Móvil')}
            </Row>
          </Card>
        )}

        <Card title="Notas" style={{ marginBottom: 16 }}>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <Form.Item>
                <Input.TextArea {...field} value={field.value ?? ''} rows={4} />
              </Form.Item>
            )}
          />
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            {isEdit ? 'Guardar cambios' : 'Crear cuenta'}
          </Button>
          <Button onClick={() => navigate(-1)}>Cancelar</Button>
        </Space>
      </Form>
    </>
  );
}
